import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSession } from '@/App';
import { Globe, Building2, Home, Search, AlertCircle } from 'lucide-react';

interface OrganizationItem {
  id: number;
  name: string;
  organization_id: number;
}

interface HomeItem {
  id: number;
  name: string;
  organization_id: number | null;
  organization_name: string | null;
}

interface ApiResponse<T> {
  total: number;
  items: T[];
}

interface VisibilityEditorProps {
  templateData: any;
  organizations: OrganizationItem[]; // Keep for backward compatibility
  homes: HomeItem[]; // Keep for backward compatibility
  onChange: (updates: any) => void;
}

export function VisibilityEditor({ 
  templateData, 
  onChange 
}: VisibilityEditorProps) {
  const { session, isLoading: sessionLoading } = useSession();
  
  // Get user from session 
  const effectiveUser = session?.user || null;
  
  // Search states
  const [orgSearch, setOrgSearch] = useState('');
  const [homeSearch, setHomeSearch] = useState('');
  const [selectedOrgForHomeFilter, setSelectedOrgForHomeFilter] = useState<number | null>(null);
  
  // Fetch organizations with search support
  const { data: organizationsData, isLoading: orgsLoading } = useQuery<ApiResponse<OrganizationItem>>({
    queryKey: ['/api/organizations', { search: orgSearch, limit: 50, offset: 0 }],
    queryFn: async () => {
      const params = new URLSearchParams({
        search: orgSearch,
        limit: '50',
        offset: '0'
      });
      const response = await fetch(`/api/organizations?${params}`);
      return response.json();
    }
  });
  
  // Fetch homes with search and organization filter support
  const { data: homesData, isLoading: homesLoading } = useQuery<ApiResponse<HomeItem>>({
    queryKey: ['/api/homes', { 
      search: homeSearch, 
      limit: 50, 
      offset: 0, 
      organizationId: selectedOrgForHomeFilter 
    }],
    queryFn: async () => {
      const params = new URLSearchParams({
        search: homeSearch,
        limit: '50',
        offset: '0'
      });
      if (selectedOrgForHomeFilter) {
        params.append('organizationId', selectedOrgForHomeFilter.toString());
      }
      const response = await fetch(`/api/homes?${params}`);
      return response.json();
    }
  });

  const organizations = organizationsData?.items || [];
  const homes = homesData?.items || [];

  const updateOwnership = (ownerType: string) => {
    const updates: any = { 
      owner_type: ownerType,
      owner_org: null,
      owner_home: null,
      hidden_from: []
    };

    // Set default ownership based on effectiveUser context
    if (ownerType === 'Organization' && effectiveUser?.organization_id) {
      updates.owner_org = effectiveUser.organization_id;
    } else if (ownerType === 'Home' && effectiveUser?.home_id) {
      updates.owner_home = effectiveUser.home_id;
    }

    onChange(updates);
  };

  const updateHiddenFromHomes = (homeId: number, isHidden: boolean) => {
    const hiddenFrom = templateData.hidden_from || [];
    
    if (isHidden && !hiddenFrom.includes(homeId)) {
      onChange({ hidden_from: [...hiddenFrom, homeId] });
    } else if (!isHidden && hiddenFrom.includes(homeId)) {
      onChange({ hidden_from: hiddenFrom.filter((id: number) => id !== homeId) });
    }
  };

  const getOwnershipIcon = (type: string) => {
    switch (type) {
      case 'Global': return Globe;
      case 'Organization': return Building2;
      case 'Home': return Home;
      default: return Globe;
    }
  };

  const getOwnershipDescription = (type: string) => {
    switch (type) {
      case 'Global': 
        return 'Available to all organizations and homes across the platform';
      case 'Organization': 
        return 'Available to all homes within the selected organization';
      case 'Home': 
        return 'Available only to the selected care home';
      default: 
        return '';
    }
  };

  // Check Global template permissions using session data only
  const userRole = effectiveUser?.role;
  
  // Normalize role for comparison (case-insensitive)
  const normalizedRole = (userRole || '').toLowerCase().replace(/[_\s]/g, '');
  
  // Grant permissions if user has proper role (more permissive)
  const canSetGlobal = effectiveUser && (
    normalizedRole === 'developer' || normalizedRole === 'platformmanager'
  );
  
  // Simple loading state - only if session is actively loading
  if (sessionLoading && !session) {
    return (
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }
  
  
  
  // Validation helpers
  const isValid = () => {
    if (templateData.owner_type === 'Organization' && !templateData.owner_org) return false;
    if (templateData.owner_type === 'Home' && !templateData.owner_home) return false;
    return true;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Template Ownership & Visibility</CardTitle>
        <p className="text-sm text-muted-foreground">
          Control who can access and use this template
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Template Ownership Type */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <Label className="text-base font-medium">Template Ownership</Label>
            {!isValid() && (
              <AlertCircle className="w-4 h-4 text-destructive" />
            )}
          </div>
          <RadioGroup 
            value={templateData.owner_type} 
            onValueChange={updateOwnership}
            className="space-y-3"
            data-testid="radio-group-ownership"
          >
            {canSetGlobal && (
              <div className="flex items-start space-x-3 p-3 border rounded-lg">
                <RadioGroupItem 
                  value="Global" 
                  id="global" 
                  className="mt-1"
                  data-testid="radio-ownership-global"
                />
                <div className="space-y-1 flex-1">
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4 text-blue-600" />
                    <Label htmlFor="global" className="font-medium">Global Template</Label>
                    <Badge variant="outline" className="text-xs">Platform Level</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {getOwnershipDescription('Global')}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-3 p-3 border rounded-lg">
              <RadioGroupItem 
                value="Organization" 
                id="organization" 
                className="mt-1"
                data-testid="radio-ownership-organization"
              />
              <div className="space-y-1 flex-1">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4 text-green-600" />
                  <Label htmlFor="organization" className="font-medium">Organization Template</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  {getOwnershipDescription('Organization')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 border rounded-lg">
              <RadioGroupItem 
                value="Home" 
                id="home" 
                className="mt-1"
                data-testid="radio-ownership-home"
              />
              <div className="space-y-1 flex-1">
                <div className="flex items-center space-x-2">
                  <Home className="w-4 h-4 text-purple-600" />
                  <Label htmlFor="home" className="font-medium">Home Template</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  {getOwnershipDescription('Home')}
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        <Separator />

        {/* Global Template Settings */}
        {templateData.owner_type === 'Global' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-blue-600" />
                <div>
                  <Label className="text-base font-medium">Available to all users</Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    This template will be available across all organizations and homes
                  </p>
                </div>
              </div>
              <Checkbox 
                checked={true} 
                disabled={true}
                data-testid="checkbox-global-availability"
              />
            </div>

            {/* Hidden From Homes */}
            <div>
              <Label className="text-base font-medium">Hidden From Homes (Optional)</Label>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                Select specific homes that should not have access to this global template
              </p>
              
              {/* Search for homes */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search homes..."
                  value={homeSearch}
                  onChange={(e) => setHomeSearch(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-homes"
                />
              </div>
              
              {homesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm text-muted-foreground">Loading homes...</span>
                </div>
              ) : homes.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-4">
                  {homeSearch ? 'No homes match your search' : 'No homes available'}
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                  {homes.map((home) => {
                    const isHidden = (templateData.hidden_from || []).includes(home.id);
                    
                    return (
                      <div key={home.id} className="flex items-center space-x-3 p-2 hover:bg-muted/50 rounded">
                        <Checkbox
                          checked={isHidden}
                          onCheckedChange={(checked) => updateHiddenFromHomes(home.id, !!checked)}
                          data-testid={`checkbox-hidden-home-${home.id}`}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{home.name}</div>
                          {home.organization_name && (
                            <div className="text-xs text-muted-foreground">
                              {home.organization_name}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Organization Template Settings */}
        {templateData.owner_type === 'Organization' && (
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Label className="text-base font-medium">Select Organization</Label>
              <span className="text-destructive text-sm">*</span>
            </div>
            
            {/* Search for organizations */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations..."
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                className="pl-10"
                data-testid="input-search-organizations"
              />
            </div>
            
            {orgsLoading ? (
              <div className="flex items-center justify-center py-4 border rounded-lg">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Loading organizations...</span>
              </div>
            ) : (
              <Select 
                value={templateData.owner_org ? templateData.owner_org.toString() : undefined} 
                onValueChange={(value) => onChange({ owner_org: parseInt(value) })}
              >
                <SelectTrigger data-testid="select-owner-organization">
                  <SelectValue placeholder="Choose an organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      {orgSearch ? 'No organizations match your search' : 'No organizations available'}
                    </div>
                  ) : (
                    organizations
                      .filter(org => org.id != null) // Filter out invalid entries
                      .map((org) => (
                        <SelectItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
            )}
            
            {!templateData.owner_org && (
              <p className="text-sm text-destructive mt-2">Please select an organization</p>
            )}
          </div>
        )}

        {/* Home Template Settings */}
        {templateData.owner_type === 'Home' && (
          <div className="space-y-4">
            {/* Optional Organization Filter */}
            <div>
              <Label className="text-base font-medium">Filter by Organization (Optional)</Label>
              <p className="text-sm text-muted-foreground mt-1 mb-2">
                Select an organization to filter the available homes
              </p>
              
              <Select 
                value={selectedOrgForHomeFilter ? selectedOrgForHomeFilter.toString() : 'all'} 
                onValueChange={(value) => {
                  const orgId = value === 'all' ? null : parseInt(value);
                  setSelectedOrgForHomeFilter(orgId);
                }}
              >
                <SelectTrigger data-testid="select-home-organization-filter">
                  <SelectValue placeholder="All organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All organizations</SelectItem>
                  {organizations
                    .filter(org => org.id != null) // Filter out invalid entries
                    .map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Home Selection */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Label className="text-base font-medium">Select Care Home</Label>
                <span className="text-destructive text-sm">*</span>
              </div>
              
              {/* Search for homes */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search homes..."
                  value={homeSearch}
                  onChange={(e) => setHomeSearch(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-homes-owner"
                />
              </div>
              
              {homesLoading ? (
                <div className="flex items-center justify-center py-4 border rounded-lg">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm text-muted-foreground">Loading homes...</span>
                </div>
              ) : (
                <Select 
                  value={templateData.owner_home ? templateData.owner_home.toString() : undefined} 
                  onValueChange={(value) => onChange({ owner_home: parseInt(value) })}
                >
                  <SelectTrigger data-testid="select-owner-home">
                    <SelectValue placeholder="Choose a care home" />
                  </SelectTrigger>
                  <SelectContent>
                    {homes.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        {homeSearch ? 'No homes match your search' : 'No homes available'}
                      </div>
                    ) : (
                      homes
                        .filter(home => home.id != null) // Filter out invalid entries
                        .map((home) => (
                          <SelectItem key={home.id} value={String(home.id)}>
                            <div>
                              <div>{home.name}</div>
                              {home.organization_name && (
                                <div className="text-xs text-muted-foreground">
                                  {home.organization_name}
                                </div>
                              )}
                            </div>
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
              )}
              
              {!templateData.owner_home && (
                <p className="text-sm text-destructive mt-2">Please select a care home</p>
              )}
            </div>
          </div>
        )}

        {/* Visibility Summary */}
        <div className={`p-4 rounded-lg border ${isValid() ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800'}`}>
          <h4 className="font-medium mb-2 flex items-center space-x-2">
            <span>Visibility Summary</span>
            {isValid() ? (
              <Badge variant="outline" className="text-green-700 border-green-300">Valid</Badge>
            ) : (
              <Badge variant="outline" className="text-yellow-700 border-yellow-300">Incomplete</Badge>
            )}
          </h4>
          <div className="text-sm space-y-1" data-testid="visibility-summary">
            {templateData.owner_type === 'Global' && (
              <>
                <p>✓ Available to all organizations and homes</p>
                {(templateData.hidden_from || []).length > 0 && (
                  <p>⚠️ Hidden from {templateData.hidden_from.length} home(s)</p>
                )}
              </>
            )}
            
            {templateData.owner_type === 'Organization' && (
              <>
                {templateData.owner_org ? (
                  <p>✓ Available to organization: <strong>{
                    organizations.find(org => org.id === templateData.owner_org)?.name || 'Unknown'
                  }</strong></p>
                ) : (
                  <p className="text-destructive">⚠️ Please select an organization</p>
                )}
              </>
            )}
            
            {templateData.owner_type === 'Home' && (
              <>
                {templateData.owner_home ? (
                  <p>✓ Available to home: <strong>{
                    homes.find(home => home.id === templateData.owner_home)?.name || 'Unknown'
                  }</strong></p>
                ) : (
                  <p className="text-destructive">⚠️ Please select a care home</p>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}